import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LOCALIDADES_BOGOTA } from '../../../comun/data/localidades-bogota';
import { Localidad } from '../../../comun/models/localidad';
import { ParametrosService } from '../../../services/parametros.service';
import { SweetAlertService } from '../../../services/sweet-alert.service';
import { UserService } from '../../../services/user.service';
import { MicroempresarioService } from '../../services/microempresario.service';
import { PERFIL_COPY } from '../../data/perfil-microempresario.content';
import { PerfilDocumentosService } from '../documentos/perfil-documentos.service';
import { isPerfilJuridica } from '../perfil-tipo.util';
import {
  PERFIL_DOC_PASO,
  acceptFromExtensiones,
  baseValueKeyFromSlot,
  cantidadSlotsDoc,
  isMultimediaDoc,
  labelConOpcional,
  intersectSeccionDocsConSolicitud,
  isMultipleDoc,
  mergeSolicitudTiposEnSeccion,
  pruneStaleDocControls,
  slotsForDoc,
} from '../documentos/perfil-documentos.util';
import type { PerfilDocSlot } from '../documentos/perfil-documentos.util';
import {
  canRemoveArchivoSlot,
  emptyArchivoSlot,
  isArchivoSlotRechazado,
  isArchivoSlotValidoParaProgreso,
  PerfilArchivoSlotState,
  shouldShowArchivoSlotAprobadoUi,
  slotStateFromUploadResult,
  slotStateFromUploaded,
} from '../documentos/perfil-archivo-slot.util';
import {
  findRedDetalleForCatalogo,
  mapEmpresarioToSobreTi,
  mapEmpresarioToSobreTiJuridica,
} from '../progress/perfil-progress-hydrate';
import { PerfilDocumentoDef } from '../progress/perfil-progress.definitions';
import { PerfilProgressService } from '../progress/perfil-progress.service';
import {
  PerfilSectionDirtyTracker,
  serializePerfilSectionState,
} from '../progress/perfil-section-dirty';
import {
  PERFIL_HUB_ROUTE,
  PERFIL_NAV_SHOW_INTRO,
  PERFIL_PASO_QUERY,
  consumeShowIntroFlag,
  invalidFilledControlKeys,
  markInvalidControlsTouched,
  navigateAfterSectionSave,
  parsePerfilPasoQuery,
  readPerfilNavState,
  resolveNextSectionId,
  resolvePerfilSectionCtaKind,
  resolvePerfilStepCtaKind,
} from '../progress/perfil-section-nav';
import { PerfilSeccionAccesoService } from '../progress/perfil-seccion-acceso.service';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import { isFechaRegistroCamaraInvalida } from '../segmento/perfil-segmento.util';
import { PerfilDocumentoModalConfig } from '../shared/perfil-documento-modal/perfil-documento-modal.model';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import { PerfilEmpresarioCache } from '../solicitud/perfil-solicitud.models';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

declare var $: any;

/** Edad máxima fallback (años) si no hay parámetro backend aún. */
const EDAD_MAXIMA_FALLBACK = 100;
const FECHA_MINIMA_SISTEMA = new Date(1900, 0, 1);
const ID_BOGOTA = 11001;

interface CatalogoItem {
  id: number | string;
  nombre: string;
}

interface CiiuItem {
  codigoCiiu: string;
  nombreCiiu: string;
}

interface SobreTiStep {
  id: string;
  controls: string[];
}

interface RedSocialItem {
  id: string;
  label: string;
  idCatalogo?: number;
  esOtro?: boolean;
}

interface CatalogoRedSocialApi {
  idCatalogoRedSocial?: number;
  codigo?: string;
  nombre?: string;
  orden?: number;
  esOtro?: boolean;
}

/** Antepone https:// solo si la URL no trae ya http:// o https://. */
function normalizeRedSocialUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/** J.4: no duplicar upload de cámara en opcionales. */
function isCamaraComercioDocLabel(label: string | undefined): boolean {
  const t = (label || '').toLowerCase();
  return (
    t.includes('cámara de comercio') ||
    t.includes('camara de comercio') ||
    (t.includes('camara') && t.includes('comercio'))
  );
}

/**
 * Formulario "Sobre ti" (PN) / "Sobre la empresa y tu" (PJ).
 * PN: un solo scroll. PJ: multi-paso datos-empresa → fotos/docs → info-adicional.
 * Documentos: slots dinámicos según catálogo admin.
 */
@Component({
  selector: 'app-perfil-sobre-ti',
  templateUrl: './perfil-sobre-ti.component.html',
  styleUrls: ['./perfil-sobre-ti.component.scss'],
  standalone: false,
})
export class PerfilSobreTiComponent implements OnInit, OnDestroy {
  readonly copy = PERFIL_COPY;
  readonly archivoIcon =
    'assets/img/perfil/icon-archivo-cargado.svg';
  readonly fotoArchivoIcon =
    'assets/img/perfil/icon-archivo-cargado.svg';
  readonly checkIcon = 'assets/img/perfil/icon-check.svg';
  readonly idBogota = ID_BOGOTA;
  readonly esJuridica: boolean;
  readonly fechaActual = new Date().toLocaleDateString('en-CA');

  form!: UntypedFormGroup;
  /** PN: todos los docs. PJ: docs no-foto del paso fotos (obligatorios). */
  documentosSeccion: PerfilDocumentoDef[] = [];
  /** PJ: fotos del negocio. */
  fotoDocs: PerfilDocumentoDef[] = [];
  /** PJ: opcionales (video/multimedia) sin cámara duplicada. */
  documentosInfoAdicional: PerfilDocumentoDef[] = [];
  fotos: PerfilArchivoSlotState[] = [];
  docsFiles: Record<string, PerfilArchivoSlotState> = {};
  redesItems: RedSocialItem[] = [];
  docModalOpen = false;
  docModalConfig: PerfilDocumentoModalConfig | null = null;
  private docModalValueKey: string | null = null;
  private pendingDocInput: HTMLInputElement | null = null;
  salirModalOpen = false;
  introModalOpen = false;
  saving = false;
  fechaCamaraHintOpen = false;
  fechaCamaraHintStyle: Record<string, string> = {};

  generos: CatalogoItem[] = [];
  estadosCivil: CatalogoItem[] = [];
  nivelesEstudios: CatalogoItem[] = [];
  gruposSisben: CatalogoItem[] = [];
  departamentos: CatalogoItem[] = [];
  ciudades: CatalogoItem[] = [];
  localidades: ReadonlyArray<Localidad> = LOCALIDADES_BOGOTA;
  tiposSociedad: CatalogoItem[] = [];
  ciius: CiiuItem[] = [];

  steps: SobreTiStep[] = [];
  currentStep = 0;

  private readonly dirtyTracker = new PerfilSectionDirtyTracker();
  /** Kind mostrado al usuario; se congela para que un blur previo al click no dispare guardado. */
  private displayedCtaKind: 'continuar' | 'guardar' = 'guardar';

  fechaNacMax = '';
  fechaNacMin = '';
  fechaExpMax = '';
  fechaDesembolsoMax = '';

  @ViewChild('sectionScroll') sectionScroll?: ElementRef<HTMLElement>;
  @ViewChildren('docInput') docInputs?: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('fotoInput') fotoInputs?: QueryList<ElementRef<HTMLInputElement>>;

  private progressSub?: Subscription;
  private docsSub?: Subscription;
  private redesSub?: Subscription;
  private modalSub?: Subscription;
  private readonly loadSub = new Subscription();
  private pendingPaso: number | null = null;
  private hydratingUbicacion = true;
  private departamentosCargados = false;
  private pendingEmpUbicacion: PerfilEmpresarioCache | null | undefined;
  private readonly showIntroOnEnter: boolean;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sweetAlert: SweetAlertService,
    private readonly progress: PerfilProgressService,
    private readonly documentosService: PerfilDocumentosService,
    private readonly parametros: ParametrosService,
    private readonly solicitudService: PerfilSolicitudService,
    private readonly seccionAcceso: PerfilSeccionAccesoService,
    private readonly microempresario: MicroempresarioService,
    private readonly userService: UserService,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.esJuridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    this.showIntroOnEnter = !!readPerfilNavState(this.router)[
      PERFIL_NAV_SHOW_INTRO
    ];
    this.initDateBounds();
    this.form = this.esJuridica
      ? this.buildFormJuridica()
      : this.buildFormNatural();
  }

  private buildFormNatural(): UntypedFormGroup {
    return this.fb.group({
      genero: ['', Validators.required],
      fechaNacimiento: ['', Validators.required],
      fechaExpedicion: ['', Validators.required],
      estadoCivil: ['', Validators.required],
      nivelEstudios: ['', Validators.required],
      personasACargo: ['', Validators.required],
      seguridadSocial: ['', Validators.required],
      sisben: ['', Validators.required],
      idSisben: [''],
      creditoFormal: ['', Validators.required],
      fechaDesembolso: [''],
    });
  }

  private buildFormJuridica(): UntypedFormGroup {
    return this.fb.group({
      genero: ['', Validators.required],
      minoria: ['', Validators.required],
      minoriaDescripcion: [''],
      creditoFormal: ['', Validators.required],
      fechaDesembolso: [''],
      razonSocial: ['', Validators.required],
      actividadEconomica: ['', Validators.required],
      tieneCamaraComercio: ['', Validators.required],
      fechaRegistroCamara: [''],
      departamento: ['', Validators.required],
      ciudad: ['', Validators.required],
      localidad: [''],
      barrio: ['', Validators.required],
      direccion: ['', Validators.required],
      tipoSociedad: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.esJuridica) {
      this.rebuildSteps();
    }
    this.docsSub = this.documentosService.ensureLoaded().subscribe((docs) => {
      const deSeccion = docs.filter((d) => d.seccionId === 'sobre-ti');
      this.applyDocumentos(deSeccion);
      this.precargarDesdeBackend();
    });
    if (this.esJuridica) {
      this.cargarCatalogosJuridica();
      this.syncMinoriaValidators(this.form.get('minoria')?.value);
      this.syncCreditoFormalValidators(this.form.get('creditoFormal')?.value);
      this.syncFechaCamaraValidators(
        this.form.get('tieneCamaraComercio')?.value
      );
      this.syncLocalidadValidators();
      this.cargarRedesSociales();
      this.loadSub.add(
        this.form.get('minoria')!.valueChanges.subscribe((value) => {
          this.syncMinoriaValidators(value);
        })
      );
      this.loadSub.add(
        this.form
          .get('tieneCamaraComercio')!
          .valueChanges.subscribe((value) =>
            this.syncFechaCamaraValidators(value)
          )
      );
      this.loadSub.add(
        this.form
          .get('fechaRegistroCamara')!
          .valueChanges.subscribe(() => this.revalidateFechaCamaraAntiguedad())
      );
      this.loadSub.add(
        this.form
          .get('ciudad')!
          .valueChanges.subscribe(() => this.syncLocalidadValidators())
      );
      this.loadSub.add(
        this.segmentoService.ensureParamsLoaded().subscribe(() => {
          this.syncFechaCamaraValidators(
            this.form.get('tieneCamaraComercio')?.value
          );
        })
      );
    } else {
      this.cargarCatalogos();
      this.syncSisbenValidators(this.form.get('sisben')?.value);
      this.syncCreditoFormalValidators(this.form.get('creditoFormal')?.value);
      this.loadSub.add(
        this.form.get('sisben')!.valueChanges.subscribe((value) => {
          this.syncSisbenValidators(value);
        })
      );
    }
    this.syncProgress();
    this.progressSub = this.form.valueChanges.subscribe(() => {
      this.syncProgress();
    });
    this.loadSub.add(
      this.form.get('creditoFormal')!.valueChanges.subscribe((value) => {
        this.syncCreditoFormalValidators(value);
      })
    );
    this.loadSub.add(
      this.route.queryParamMap.subscribe((params) => {
        const paso = parsePerfilPasoQuery(params.get(PERFIL_PASO_QUERY));
        if (this.esJuridica) {
          this.pendingPaso = paso;
          this.applyPendingPaso();
          return;
        }
        if (paso == null) {
          return;
        }
        setTimeout(() => this.scrollToPaso(paso), 0);
      })
    );
  }

  private scrollToPaso(paso: number): void {
    const scroller = this.sectionScroll?.nativeElement;
    if (!scroller) {
      return;
    }
    const targetId =
      paso >= 1 ? 'perfil-sobre-ti-docs' : 'perfil-sobre-ti-datos';
    const target = scroller.querySelector<HTMLElement>(`#${targetId}`);
    scroller.scrollTop = target ? Math.max(0, target.offsetTop - 8) : 0;
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
    this.docsSub?.unsubscribe();
    this.redesSub?.unsubscribe();
    this.modalSub?.unsubscribe();
    this.loadSub.unsubscribe();
  }

  get currentStepId(): string {
    return this.steps[this.currentStep]?.id ?? '';
  }

  get isLastStep(): boolean {
    return this.currentStep >= this.steps.length - 1;
  }

  get sectionPercent(): number {
    return this.progress.getSectionProgress('sobre-ti').percent;
  }

  get sectionDirty(): boolean {
    return this.dirtyTracker.isDirty(() => this.serializeSectionState());
  }

  get ctaKind(): 'continuar' | 'guardar' {
    if (this.esJuridica) {
      return resolvePerfilStepCtaKind(
        this.isLastStep,
        this.sectionPercent,
        this.sectionDirty
      );
    }
    const kind = resolvePerfilSectionCtaKind(
      this.sectionPercent,
      this.sectionDirty
    );
    this.displayedCtaKind = kind;
    return kind;
  }

  get ctaLabel(): string {
    return this.ctaKind === 'continuar'
      ? this.copy.botones.continuar
      : this.copy.botones.guardarInformacion;
  }

  get continuarDisabled(): boolean {
    if (this.saving) {
      return true;
    }
    if (this.esJuridica) {
      if (this.isLastStep && this.ctaKind === 'continuar') {
        return false;
      }
      return !this.isCurrentStepValid();
    }
    if (this.ctaKind === 'continuar') {
      return false;
    }
    return this.form.invalid;
  }

  get showSaltar(): boolean {
    return true;
  }

  get muestraGrupoSisben(): boolean {
    return this.form.get('sisben')?.value === 'si';
  }

  get muestraFechaDesembolso(): boolean {
    return this.form.get('creditoFormal')?.value === 'si';
  }

  get muestraLocalidad(): boolean {
    return Number(this.form?.get('ciudad')?.value) === this.idBogota;
  }

  get muestraFechaCamara(): boolean {
    return this.form?.get('tieneCamaraComercio')?.value === 'si';
  }

  get muestraMinoriaDescripcion(): boolean {
    return this.form?.get('minoria')?.value === 'si';
  }

  get mensajeFechaRegistro(): string {
    return this.segmentoService.paramsSnapshot.mensajeFechaRegistro || '';
  }

  acceptFor(doc: PerfilDocumentoDef): string {
    if (isMultimediaDoc(doc)) {
      return acceptFromExtensiones(doc.extensionesValidas) || 'video/*,image/*';
    }
    return acceptFromExtensiones(doc.extensionesValidas);
  }

  labelDoc(doc: PerfilDocumentoDef): string {
    return labelConOpcional(doc.label, doc.obligatorio);
  }

  slotsFor(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc);
  }

  isDocMultiple(doc: PerfilDocumentoDef): boolean {
    return isMultipleDoc(doc);
  }

  maxSlotsCount(doc: PerfilDocumentoDef): number {
    return cantidadSlotsDoc(doc);
  }

  filledSlots(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc).filter((s) => this.hasSlotDoc(s.slotKey));
  }

  filledSlotsCount(doc: PerfilDocumentoDef): number {
    return this.filledSlots(doc).length;
  }

  nextEmptySlot(doc: PerfilDocumentoDef): PerfilDocSlot | null {
    return slotsForDoc(doc).find((s) => !this.hasSlotDoc(s.slotKey)) ?? null;
  }

  multiUploadCta(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreTi.cargarArchivos
      : this.copy.sobreTi.cargarOtroArchivo;
  }

  multiUploadHint(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreTi.puedesSubirVarios
      : this.copy.sobreTi.archivosCargados;
  }

  private findDocBySlotOrValueKey(
    slotOrValueKey: string
  ): PerfilDocumentoDef | undefined {
    const base = baseValueKeyFromSlot(slotOrValueKey);
    return (
      this.documentosSeccion.find((d) => d.valueKey === base) ??
      this.documentosInfoAdicional.find((d) => d.valueKey === base) ??
      this.fotoDocs.find((d) => d.valueKey === base)
    );
  }

  private syncDocProgressControl(doc: PerfilDocumentoDef): void {
    const filled = slotsForDoc(doc).find((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
    const name = filled ? this.docsFiles[filled.slotKey].name : '';
    this.form.get(doc.valueKey)?.setValue(name, { emitEvent: false });
    this.form.get(doc.valueKey)?.markAsTouched();
    this.form.get(doc.valueKey)?.updateValueAndValidity({ emitEvent: false });
  }

  hasDoc(valueKey: string): boolean {
    const doc = this.findDocBySlotOrValueKey(valueKey);
    if (!doc) {
      return isArchivoSlotValidoParaProgreso(this.docsFiles[valueKey]);
    }
    return slotsForDoc(doc).some((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
  }

  hasSlotDoc(slotKey: string): boolean {
    return !!this.docsFiles[slotKey]?.name;
  }

  isSlotRechazado(slotKey: string): boolean {
    return isArchivoSlotRechazado(this.docsFiles[slotKey]);
  }

  isSlotAprobadoUi(slotKey: string): boolean {
    return shouldShowArchivoSlotAprobadoUi(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  canRemoveSlot(slotKey: string): boolean {
    return canRemoveArchivoSlot(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  slotStatusLabel(slotKey: string): string {
    if (this.isSlotRechazado(slotKey)) {
      return this.copy.sobreTi.cedulaArchivoRechazado;
    }
    if (this.isSlotAprobadoUi(slotKey)) {
      return this.copy.sobreTi.cedulaArchivoAprobado;
    }
    return this.copy.sobreTi.cedulaArchivoSubido;
  }

  slotObservacion(slotKey: string): string {
    return String(this.docsFiles[slotKey]?.observacionDocumentoOCR || '').trim();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  errorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (this.esJuridica) {
      if (
        controlName === 'fechaRegistroCamara' &&
        control?.hasError('fechaRegistroAntiguedad')
      ) {
        return (
          this.mensajeFechaRegistro ||
          this.copy.sobreEmpresa.validaciones.fechaRegistroCamaraInvalida
        );
      }
      return this.copy.sobreEmpresa.validaciones.campoObligatorio;
    }
    const v = this.copy.sobreTi.validaciones;
    if (!control?.errors) {
      return v.campoObligatorio;
    }
    if (control.errors['required']) {
      return v.campoObligatorio;
    }
    if (control.errors['fechaNacimiento']) {
      return v.fechaNacimientoInvalida;
    }
    if (control.errors['fechaExpedicionMenor']) {
      return v.fechaExpedicionMenor;
    }
    if (control.errors['fechaExpedicionIgual']) {
      return v.fechaExpedicionIgual;
    }
    if (control.errors['fechaExpedicionIncorrecta']) {
      return v.fechaExpedicionIncorrecta;
    }
    if (control.errors['fechaMinima']) {
      return v.fechaMinima;
    }
    return v.campoObligatorio;
  }

  openDocModal(
    doc: PerfilDocumentoDef,
    slotKeyForUpload?: string,
    inputEl?: HTMLInputElement,
    ctaElementIdPrefix: string = 'btn-perfil-sobre-ti'
  ): void {
    const resolvedSlotKey = slotKeyForUpload || doc.valueKey;
    this.analyticsTag.trackCtaClick({
      ctaLabel: doc.label,
      ctaSection: 'sobre-ti',
      ctaComponent: 'perfil-sobre-ti',
      ctaElementId: `${ctaElementIdPrefix}-${resolvedSlotKey}`,
      hu: '8099',
    });
    this.modalSub?.unsubscribe();
    this.docModalValueKey = slotKeyForUpload || doc.valueKey;
    this.pendingDocInput = inputEl ?? null;
    this.modalSub = this.documentosService.modalConfigFor(doc).subscribe((cfg) => {
      this.docModalConfig = cfg;
      this.docModalOpen = true;
    });
  }

  closeDocModal(): void {
    this.docModalOpen = false;
    this.docModalConfig = null;
    this.docModalValueKey = null;
    this.pendingDocInput = null;
  }

  onSubirDesdeModal(): void {
    if (!this.docModalValueKey) {
      return;
    }
    if (this.esJuridica) {
      const fotoIndex = this.fotoSlotIndex(
        baseValueKeyFromSlot(this.docModalValueKey)
      );
      if (fotoIndex >= 0) {
        this.triggerFotoUpload(fotoIndex);
        return;
      }
    }
    if (this.pendingDocInput) {
      this.pendingDocInput.value = '';
      this.pendingDocInput.click();
      return;
    }
    this.triggerDocUpload(this.docModalValueKey);
  }

  triggerDocUpload(valueKey: string, inputEl?: HTMLInputElement): void {
    if (inputEl) {
      inputEl.value = '';
      inputEl.click();
      return;
    }
    const base = baseValueKeyFromSlot(valueKey);
    const index = this.documentosSeccion.findIndex((d) => d.valueKey === base);
    const input = this.docInputs?.get(index)?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onDocSelected(slotOrValueKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const doc = this.findDocBySlotOrValueKey(slotOrValueKey);
    if (!doc) {
      return;
    }
    const slotKey = slotOrValueKey;

    this.solicitudService
      .subirDocumento(file, doc, {
        esFoto: !!doc.esFoto,
        excludeNombreArchivo: this.docsFiles[slotKey]?.name || undefined,
      })
      .subscribe((res) => {
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo subir el archivo'
          );
          return;
        }
        if (res.estadoDocumentoOCR === 0) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error ||
              res.observacionDocumentoOCR ||
              this.copy.sobreTi.validaciones.documentosInconsistentes
          );
          this.docsFiles[slotKey] = slotStateFromUploadResult(file, res);
          this.syncDocProgressControl(doc);
          this.syncProgress();
          this.solicitudService.persistirCamposProgreso().subscribe();
          return;
        }
        void this.sweetAlert.successModal('Archivo subido');
        this.docsFiles[slotKey] = slotStateFromUploadResult(file, res);
        this.syncDocProgressControl(doc);
        if (this.docModalOpen && this.docModalValueKey === slotKey) {
          this.closeDocModal();
        }
        this.syncProgress();
        this.solicitudService.persistirCamposProgreso().subscribe();
      });
  }

  removeDoc(slotOrValueKey: string, inputEl?: HTMLInputElement): void {
    const doc = this.findDocBySlotOrValueKey(slotOrValueKey);
    const slotKey = slotOrValueKey;
    if (!this.canRemoveSlot(slotKey)) {
      return;
    }
    const idDocumento = this.docsFiles[slotKey]?.idDocumento;
    const clearLocal = (): void => {
      this.docsFiles[slotKey] = emptyArchivoSlot();
      if (doc) {
        this.syncDocProgressControl(doc);
      }
      if (inputEl) {
        inputEl.value = '';
      }
      this.syncProgress();
    };
    if (idDocumento) {
      this.solicitudService
        .eliminarDocumento(idDocumento, {
          esFoto: !!doc?.esFoto,
          idTipoDocumento: doc?.idTipoDocumento ?? doc?.id,
          nombreArchivo: this.docsFiles[slotKey]?.name || null,
        })
        .subscribe((res) => {
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo eliminar el documento'
            );
            return;
          }
          clearLocal();
          this.solicitudService.persistirCamposProgreso().subscribe();
        });
      return;
    }
    clearLocal();
    this.solicitudService.persistirCamposProgreso().subscribe();
  }

  onSelectpickerChange(field: string): void {
    setTimeout(() => {
      this.form.get(field)?.updateValueAndValidity({ emitEvent: false });
      const $el = $(`#${field}`);
      if ($el?.length && typeof $el.selectpicker === 'function') {
        $el.selectpicker('refresh');
        this.syncSelectpickerInvalidClass(field);
      }
    }, 50);
  }

  private applyDocumentos(docs: PerfilDocumentoDef[]): void {
    if (this.esJuridica) {
      this.applyDocumentosJuridica(docs);
      return;
    }
    this.documentosSeccion = docs;
    this.fotoDocs = [];
    this.documentosInfoAdicional = [];
    pruneStaleDocControls(this.form, this.docsFiles, docs);
    this.pruneStaleDocSlots(docs);

    for (const doc of this.documentosSeccion) {
      if (!this.form.contains(doc.valueKey)) {
        this.form.addControl(
          doc.valueKey,
          this.fb.control(
            '',
            doc.obligatorio ? Validators.required : []
          )
        );
      } else {
        const ctrl = this.form.get(doc.valueKey);
        if (doc.obligatorio) {
          ctrl?.setValidators([Validators.required]);
        } else {
          ctrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
      }
      for (const slot of slotsForDoc(doc)) {
        if (!this.docsFiles[slot.slotKey]) {
          this.docsFiles[slot.slotKey] = emptyArchivoSlot();
        }
      }
    }
    this.syncProgress();
  }

  private applyDocumentosJuridica(docs: PerfilDocumentoDef[]): void {
    this.fotoDocs = docs
      .filter((d) => d.esFoto || d.pasoId === PERFIL_DOC_PASO.fotos)
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    const noFoto = docs
      .filter((d) => !(d.esFoto || d.pasoId === PERFIL_DOC_PASO.fotos))
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    this.documentosInfoAdicional = noFoto.filter(
      (d) =>
        d.pasoId === PERFIL_DOC_PASO.infoAdicional &&
        !isCamaraComercioDocLabel(d.label)
    );
    this.documentosSeccion = noFoto.filter(
      (d) => d.pasoId !== PERFIL_DOC_PASO.infoAdicional
    );
    const allDocs = [
      ...this.fotoDocs,
      ...this.documentosSeccion,
      ...this.documentosInfoAdicional,
    ];
    pruneStaleDocControls(this.form, this.docsFiles, allDocs);
    this.pruneStaleDocSlots([...this.documentosSeccion, ...this.documentosInfoAdicional]);

    while (this.fotos.length < this.fotoDocs.length) {
      this.fotos.push(emptyArchivoSlot());
    }
    this.fotos = this.fotos.slice(0, Math.max(this.fotoDocs.length, 0));

    for (const doc of allDocs) {
      if (!this.form.contains(doc.valueKey)) {
        this.form.addControl(
          doc.valueKey,
          this.fb.control('', doc.obligatorio ? Validators.required : [])
        );
      } else {
        const ctrl = this.form.get(doc.valueKey);
        if (doc.obligatorio) {
          ctrl?.setValidators([Validators.required]);
        } else {
          ctrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
      }
      if (!doc.esFoto && doc.pasoId !== PERFIL_DOC_PASO.fotos) {
        for (const slot of slotsForDoc(doc)) {
          if (!this.docsFiles[slot.slotKey]) {
            this.docsFiles[slot.slotKey] = emptyArchivoSlot();
          }
        }
      }
    }
    this.rebuildSteps();
    this.syncProgress();
  }

  private pruneStaleDocSlots(keepDocs: ReadonlyArray<PerfilDocumentoDef>): void {
    const keep = new Set<string>();
    for (const doc of keepDocs) {
      for (const slot of slotsForDoc(doc)) {
        keep.add(slot.slotKey);
      }
    }
    for (const key of Object.keys(this.docsFiles)) {
      if (key.startsWith('doc_') && !keep.has(key)) {
        delete this.docsFiles[key];
      }
    }
  }

  private syncProgress(): void {
    this.progress.updateSectionValues(
      'sobre-ti',
      this.form.getRawValue(),
      invalidFilledControlKeys(this.form)
    );
  }

  private cargarCatalogos(): void {
    this.parametros.getGeneros().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.generos = list.map(
        (g: { idGenero: number | string; nombre: string }) => ({
          id: g.idGenero,
          nombre: g.nombre,
        })
      );
      setTimeout(() => this.refreshSelectpicker('genero'), 200);
    });
    this.parametros.getEstadoCivil().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.estadosCivil = list.map(
        (e: { idEstadoCivil: number; nombre: string }) => ({
          id: e.idEstadoCivil,
          nombre: e.nombre,
        })
      );
      setTimeout(() => this.refreshSelectpicker('estadoCivil'), 200);
    });
    this.parametros.getNivelEstudios().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.nivelesEstudios = list.map(
        (n: { idNivelEstudios: number; nombre: string }) => ({
          id: n.idNivelEstudios,
          nombre: n.nombre,
        })
      );
      setTimeout(() => this.refreshSelectpicker('nivelEstudios'), 200);
    });
    this.microempresario.getClasificacionSisben().subscribe({
      next: (data: any) => {
        const list = Array.isArray(data) ? data : data?.data ?? [];
        this.gruposSisben = list.map(
          (s: { idSisben: number; grupoPoblacional: string }) => ({
            id: s.idSisben,
            nombre: s.grupoPoblacional,
          })
        );
      },
      error: () => {
        this.gruposSisben = [];
      },
    });
  }

  private syncSisbenValidators(value: unknown): void {
    const ctrl = this.form.get('idSisben');
    if (!ctrl) {
      return;
    }
    if (value === 'si') {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private syncCreditoFormalValidators(value: unknown): void {
    const ctrl = this.form.get('fechaDesembolso');
    if (!ctrl) {
      return;
    }
    if (value === 'si') {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private precargarDesdeBackend(): void {
    this.loadSub.add(
      this.solicitudService.ensureCargado(true).subscribe((carga) => {
        const emp = carga.empresario;
        if (emp) {
          const patch = this.esJuridica
            ? mapEmpresarioToSobreTiJuridica(emp)
            : mapEmpresarioToSobreTi(emp);
          if (Object.keys(patch).length) {
            this.form.patchValue(patch, { emitEvent: false });
            if (this.esJuridica) {
              this.syncMinoriaValidators(this.form.get('minoria')?.value);
              this.syncCreditoFormalValidators(
                this.form.get('creditoFormal')?.value
              );
              this.syncFechaCamaraValidators(
                this.form.get('tieneCamaraComercio')?.value
              );
              this.pendingEmpUbicacion = emp;
              this.tryHydrateUbicacion();
            } else {
              this.syncSisbenValidators(this.form.get('sisben')?.value);
              this.syncCreditoFormalValidators(
                this.form.get('creditoFormal')?.value
              );
            }
            markInvalidControlsTouched(this.form);
            this.syncProgress();
          }
          setTimeout(() => {
            this.reapplySelectFromForm('genero');
            if (!this.esJuridica) {
              this.reapplySelectFromForm('estadoCivil');
              this.reapplySelectFromForm('nivelEstudios');
            } else {
              this.reapplySelectFromForm('actividadEconomica');
              this.reapplySelectFromForm('tipoSociedad');
              this.reapplySelectFromForm('departamento');
              this.reapplySelectFromForm('ciudad');
              this.reapplySelectFromForm('localidad');
            }
            this.refreshAllSelectpickers();
            if (this.esJuridica) {
              this.scheduleRebindUbicacionSelectpickers();
            }
            this.scheduleBaselineAfterUiSettle();
          }, 300);
        } else if (this.esJuridica) {
          this.pendingEmpUbicacion = null;
          this.tryHydrateUbicacion();
        }
        if (this.esJuridica) {
          this.applyRedesFromBackend();
        }
        this.precargarDocumentosCargados();
      })
    );
  }

  private precargarDocumentosCargados(): void {
    this.solicitudService.cargarDocumentosSolicitud().subscribe((data) => {
      let filtered = intersectSeccionDocsConSolicitud(
        this.documentosService.documentos,
        data,
        'sobre-ti',
        { juridica: this.esJuridica }
      );
      if (this.esJuridica) {
        filtered = mergeSolicitudTiposEnSeccion(filtered, data, 'sobre-ti', {
          juridica: true,
        });
      }
      this.applyDocumentos(filtered);
      this.progress.replaceDocumentosDeSeccion('sobre-ti', filtered);
      if (this.esJuridica) {
        this.hydrateUploadedJuridica(data);
      } else {
        for (const item of [...data.archivos, ...data.fotos]) {
          const def = this.documentosSeccion.find(
            (d) =>
              Number(d.idTipoDocumento ?? d.id) === Number(item.idTipoDocumento)
          );
          if (!def) {
            continue;
          }
          const files = (item.neoDocumentosSolicitudDTO ?? []).filter(
            (f) => !!f?.idDocumentoSolicitud
          );
          const slots = slotsForDoc(def);
          for (let idx = 0; idx < slots.length; idx++) {
            const uploaded = files[idx];
            const slotKey = slots[idx].slotKey;
            if (!uploaded) {
              this.docsFiles[slotKey] = emptyArchivoSlot();
              continue;
            }
            this.docsFiles[slotKey] = slotStateFromUploaded(
              uploaded,
              uploaded.nombreDocumento ||
                String(this.form.get(def.valueKey)?.value || 'Documento')
            );
          }
          this.syncDocProgressControl(def);
        }
      }
      markInvalidControlsTouched(this.form);
      this.syncProgress();
      this.solicitudService.persistirCamposProgreso().subscribe();
      this.markSectionBaseline();
      this.scheduleBaselineAfterUiSettle();
      if (this.esJuridica) {
        this.maybeOpenIntroModal();
      }
    });
  }

  private hydrateUploadedJuridica(data: {
    archivos: Array<{
      idTipoDocumento?: number;
      neoDocumentosSolicitudDTO?: Array<{
        idDocumentoSolicitud?: string | number;
        nombreDocumento?: string;
        [key: string]: unknown;
      }>;
    }>;
    fotos: Array<{
      idTipoDocumento?: number;
      neoDocumentosSolicitudDTO?: Array<{
        idDocumentoSolicitud?: string | number;
        nombreDocumento?: string;
        [key: string]: unknown;
      }>;
    }>;
  }): void {
    const applyList = (
      list: typeof data.archivos,
      docs: PerfilDocumentoDef[],
      isFoto: boolean
    ): void => {
      for (const item of list ?? []) {
        const def = docs.find(
          (d) =>
            Number(d.idTipoDocumento ?? d.id) === Number(item.idTipoDocumento)
        );
        if (!def) {
          continue;
        }
        const files = (item.neoDocumentosSolicitudDTO ?? []).filter(
          (f) => !!f?.idDocumentoSolicitud
        );
        if (isFoto) {
          const uploaded = files[0];
          if (!uploaded) {
            continue;
          }
          const name =
            uploaded.nombreDocumento ||
            String(this.form.get(def.valueKey)?.value || 'Documento');
          this.form.get(def.valueKey)?.setValue(name, { emitEvent: false });
          const idx = this.fotoSlotIndex(def.valueKey);
          if (idx >= 0) {
            this.fotos[idx] = slotStateFromUploaded(uploaded as never, name);
            if (!isArchivoSlotValidoParaProgreso(this.fotos[idx])) {
              this.form.get(def.valueKey)?.setValue('', { emitEvent: false });
            }
          }
          continue;
        }
        const slots = slotsForDoc(def);
        for (let idx = 0; idx < slots.length; idx++) {
          const uploaded = files[idx];
          const slotKey = slots[idx].slotKey;
          if (!uploaded) {
            this.docsFiles[slotKey] = emptyArchivoSlot();
            continue;
          }
          this.docsFiles[slotKey] = slotStateFromUploaded(
            uploaded as never,
            uploaded.nombreDocumento ||
              String(this.form.get(def.valueKey)?.value || 'Documento')
          );
        }
        this.syncDocProgressControl(def);
      }
    };
    applyList(data.fotos, this.fotoDocs, true);
    applyList(data.archivos, this.documentosSeccion, false);
    applyList(data.archivos, this.documentosInfoAdicional, false);
  }

  private serializeSectionState(): string {
    if (!this.esJuridica) {
      return serializePerfilSectionState(
        this.form.getRawValue(),
        this.docsFiles
      );
    }
    const docs: Record<
      string,
      { idDocumento?: number | string | null; name?: string }
    > = { ...this.docsFiles };
    this.fotoDocs.forEach((doc, i) => {
      const slot = this.fotos[i];
      if (doc?.valueKey) {
        docs[doc.valueKey] = {
          idDocumento: slot?.idDocumento,
          name: slot?.name,
        };
      }
    });
    return serializePerfilSectionState(this.form.getRawValue(), docs);
  }

  private markSectionBaseline(): void {
    this.dirtyTracker.markBaseline(() => this.serializeSectionState());
    this.form.markAsPristine();
  }

  /** Tras selectpicker/refresh: re-baseline cuando el DOM ya emitió sus change. */
  private scheduleBaselineAfterUiSettle(): void {
    setTimeout(() => this.markSectionBaseline(), 150);
  }

  onFechaNacimientoChange(): void {
    this.validarFechaMinima('fechaNacimiento');
    this.validarRangoNacimiento();
    this.validarExpedicionVsNacimiento(false);
  }

  onFechaExpedicionChange(): void {
    this.validarFechaMinima('fechaExpedicion');
    this.validarExpedicionVsNacimiento(false);
  }

  /** Al salir del campo, si la fecha ya está completa e inválida, sí alerta. */
  onFechaExpedicionBlur(): void {
    this.validarFechaMinima('fechaExpedicion');
    this.validarExpedicionVsNacimiento(true);
  }

  /**
   * CTA: Continuar (100 % sin cambios) solo navega; Guardar información persiste.
   * PN: usa displayedCtaKind. PJ: multi-paso como sobre-negocio.
   */
  onCta(): void {
    if (this.saving) {
      return;
    }
    if (this.esJuridica) {
      this.onCtaJuridica();
      return;
    }
    // Prioridad al label que el usuario vio: Continuar → nunca request.
    if (this.displayedCtaKind === 'continuar' || !this.sectionDirty) {
      this.goToNextSection();
      return;
    }
    this.onGuardar();
  }

  private onCtaJuridica(): void {
    this.markCurrentStepTouched();
    if (!this.isCurrentStepValid()) {
      return;
    }
    this.syncProgress();
    if (!this.sectionDirty) {
      if (this.isLastStep) {
        this.goToNextSection();
        return;
      }
      this.advanceToNextStep();
      return;
    }
    if (this.currentStepId === 'datos-empresa') {
      this.guardarDatosEmpresaYAvanzar();
      return;
    }
    if (this.isLastStep) {
      this.guardarRedesYContinuar();
      return;
    }
    this.advanceToNextStep();
  }

  /** Avanza al siguiente subpaso PJ (por id, no solo +1). */
  private advanceToNextStep(): void {
    const order = ['datos-empresa', 'fotos', 'info-adicional'] as const;
    const cur = order.indexOf(
      this.currentStepId as (typeof order)[number]
    );
    const nextId = order[Math.max(cur, 0) + 1] ?? order[order.length - 1];
    const idx = this.steps.findIndex((s) => s.id === nextId);
    this.currentStep = idx >= 0 ? idx : Math.min(this.currentStep + 1, this.steps.length - 1);
    this.scrollFormToTop();
    if (this.currentStepId === 'datos-empresa') {
      this.scheduleRebindUbicacionSelectpickers();
    }
  }

  onGuardar(): void {
    this.form.markAllAsTouched();
    if (!this.validarFechasAlGuardar() || this.form.invalid || this.saving) {
      return;
    }
    // Red de seguridad: sin dirty no hay nada que persistir.
    if (!this.sectionDirty) {
      this.goToNextSection();
      return;
    }
    this.syncProgress();
    this.saving = true;
    this.solicitudService.guardarBorrador(this.buildPatchNatural()).subscribe((res) => {
      this.saving = false;
      if (!res.exitoso) {
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          res.error || 'No se pudo guardar la información'
        );
        return;
      }
      this.markSectionBaseline();
      this.goToNextSection();
    });
  }

  onSaltar(): void {
    this.salirModalOpen = true;
  }

  closeSalirModal(): void {
    this.salirModalOpen = false;
  }

  onSalirGuardar(): void {
    this.salirModalOpen = false;
    this.syncProgress();
    const patch = this.esJuridica
      ? {
          ...this.buildPatchJuridica(),
          redesSocialesDetalle: this.buildRedesPayload(),
        }
      : this.buildPatchNatural();
    this.solicitudService.guardarBorrador(patch).subscribe(() => {
      void this.router.navigateByUrl(PERFIL_HUB_ROUTE);
    });
  }

  closeIntroModal(): void {
    this.introModalOpen = false;
  }

  onIntroCompletarDatos(): void {
    this.closeIntroModal();
  }

  private buildPatchNatural(): Record<string, unknown> {
    const v = this.form.getRawValue();
    return {
      idGenero: v.genero,
      fechaNacimiento: v.fechaNacimiento,
      fechaExpedicionDocumento: v.fechaExpedicion,
      idEstadoCivil: Number(v.estadoCivil) || null,
      idNivelEstudios: Number(v.nivelEstudios) || null,
      personasACargo: v.personasACargo,
      seguridadSocial: v.seguridadSocial === 'si' ? 1 : 0,
      sisben: v.sisben === 'si',
      idSisben: v.sisben === 'si' ? Number(v.idSisben) || null : null,
      creditoAnterior: v.creditoFormal === 'si' ? 1 : 0,
      fechaCreditoAnterior:
        v.creditoFormal === 'si' ? v.fechaDesembolso || null : null,
    };
  }

  private buildPatchJuridica(): Record<string, unknown> {
    const v = this.form.getRawValue();
    const ciiu = this.ciius.find((c) => c.codigoCiiu === String(v.actividadEconomica));
    return {
      idGenero: v.genero,
      minoria: v.minoria === 'si' ? 1 : 0,
      minoriaDescripcion:
        v.minoria === 'si' ? v.minoriaDescripcion || null : null,
      creditoAnterior: v.creditoFormal === 'si' ? 1 : 0,
      fechaCreditoAnterior:
        v.creditoFormal === 'si' ? v.fechaDesembolso || null : null,
      razonSocial: v.razonSocial,
      codigoCiiu: v.actividadEconomica || null,
      ciiuNombre: ciiu?.nombreCiiu ?? null,
      idTipoSociedad: Number(v.tipoSociedad) || null,
      camaraComercioVigente: v.tieneCamaraComercio === 'si' ? 1 : 0,
      fechaRegistroActividadEconomica:
        v.tieneCamaraComercio === 'si' ? v.fechaRegistroCamara || null : null,
      idDepartamento: Number(v.departamento) || null,
      idCiudad: Number(v.ciudad) || null,
      idLocalidad: this.muestraLocalidad ? Number(v.localidad) || null : null,
      barrioEmpresa: v.barrio,
      direccionEmpresa: v.direccion,
      barrioResidencia: v.barrio,
      direccionResidencia: v.direccion,
    };
  }

  private goToNextSection(): void {
    const juridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    const nextId = resolveNextSectionId('sobre-ti', { juridica });
    const nextPercent = nextId
      ? Math.max(
          this.seccionAcceso.sectionPercent(nextId),
          this.progress.getSectionProgress(nextId).percent
        )
      : 0;
    navigateAfterSectionSave(this.router, 'sobre-ti', nextPercent, {
      juridica,
    });
  }

  private initDateBounds(): void {
    const hoy = new Date();
    const nacMax = new Date(hoy);
    nacMax.setFullYear(nacMax.getFullYear() - 18);
    const nacMin = new Date(hoy);
    nacMin.setFullYear(nacMin.getFullYear() - EDAD_MAXIMA_FALLBACK);

    this.fechaNacMax = this.toInputDate(nacMax);
    this.fechaNacMin = this.toInputDate(nacMin);
    this.fechaExpMax = this.toInputDate(hoy);
    this.fechaDesembolsoMax = this.toInputDate(hoy);
  }

  private toInputDate(d: Date | string | unknown): string {
    if (d instanceof Date) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
      return d.slice(0, 10);
    }
    if (d) {
      try {
        return new Date(d as string).toISOString().slice(0, 10);
      } catch {
        return '';
      }
    }
    return '';
  }

  private parseInputDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) {
      return null;
    }
    return new Date(y, m - 1, d);
  }

  private validarFechaMinima(campo: string): void {
    const control = this.form.get(campo);
    const value = control?.value as string;
    if (!value) {
      return;
    }
    const fecha = this.parseInputDate(value);
    if (!fecha) {
      return;
    }
    if (fecha.getTime() < FECHA_MINIMA_SISTEMA.getTime()) {
      control?.setErrors({ ...(control.errors || {}), fechaMinima: true });
    } else if (control?.hasError('fechaMinima')) {
      const { fechaMinima: _omit, ...rest } = control.errors || {};
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  private validarRangoNacimiento(): void {
    const control = this.form.get('fechaNacimiento');
    const value = control?.value as string;
    if (!value) {
      return;
    }
    const fecha = this.parseInputDate(value);
    const min = this.parseInputDate(this.fechaNacMin);
    const max = this.parseInputDate(this.fechaNacMax);
    if (!fecha || !min || !max) {
      return;
    }
    if (fecha < min || fecha > max) {
      control?.setErrors({ ...(control.errors || {}), fechaNacimiento: true });
    } else if (control?.hasError('fechaNacimiento')) {
      const { fechaNacimiento: _omit, ...rest } = control.errors || {};
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  private validarExpedicionVsNacimiento(showAlert: boolean): boolean {
    const nacCtrl = this.form.get('fechaNacimiento');
    const expCtrl = this.form.get('fechaExpedicion');
    const nacVal = nacCtrl?.value as string;
    const expVal = expCtrl?.value as string;
    if (!nacVal || !expVal) {
      return true;
    }

    const nacimiento = this.parseInputDate(nacVal);
    const expedicion = this.parseInputDate(expVal);
    if (!nacimiento || !expedicion) {
      return true;
    }

    const expMinima = new Date(nacimiento);
    expMinima.setFullYear(expMinima.getFullYear() + 18);

    const clearExpErrors = (): void => {
      if (!expCtrl?.errors) {
        return;
      }
      const {
        fechaExpedicionMenor: _a,
        fechaExpedicionIgual: _b,
        fechaExpedicionIncorrecta: _c,
        ...rest
      } = expCtrl.errors;
      expCtrl.setErrors(Object.keys(rest).length ? rest : null);
    };

    if (expedicion < nacimiento) {
      expCtrl?.setErrors({
        ...(expCtrl.errors || {}),
        fechaExpedicionMenor: true,
      });
      if (showAlert) {
        this.sweetAlert.errorModal(
          'Error!',
          true,
          this.copy.sobreTi.validaciones.fechaExpedicionMenor
        );
      }
      return false;
    }

    if (expedicion.getTime() === nacimiento.getTime()) {
      expCtrl?.setErrors({
        ...(expCtrl.errors || {}),
        fechaExpedicionIgual: true,
      });
      if (showAlert) {
        this.sweetAlert.errorModal(
          'Error!',
          true,
          this.copy.sobreTi.validaciones.fechaExpedicionIgual
        );
      }
      return false;
    }

    if (expedicion < expMinima) {
      expCtrl?.setErrors({
        ...(expCtrl.errors || {}),
        fechaExpedicionIncorrecta: true,
      });
      if (showAlert) {
        this.sweetAlert.errorModal(
          'Error!',
          true,
          this.copy.sobreTi.validaciones.fechaExpedicionIncorrecta
        );
      }
      return false;
    }

    clearExpErrors();
    return true;
  }

  private validarFechasAlGuardar(): boolean {
    this.validarFechaMinima('fechaNacimiento');
    this.validarFechaMinima('fechaExpedicion');
    this.validarRangoNacimiento();

    const nacCtrl = this.form.get('fechaNacimiento');
    if (nacCtrl?.hasError('fechaNacimiento')) {
      this.sweetAlert.errorModal(
        'Error!',
        true,
        this.copy.sobreTi.validaciones.fechaNacimientoInvalida
      );
      return false;
    }

    return this.validarExpedicionVsNacimiento(true);
  }

  private refreshSelectpicker(field: string): void {
    setTimeout(() => {
      const $el = $(`#${field}`);
      if ($el?.length && typeof $el.selectpicker === 'function') {
        $el.selectpicker('refresh');
        this.syncSelectpickerInvalidClass(field);
      }
    }, 50);
  }

  /**
   * El plugin mueve/copia clases NgClass del <select> al wrapper
   * (`perfil-field__control--invalid`). Hay que alinear wrapper+botón
   * con isInvalid() después del refresh.
   */
  private syncSelectpickerInvalidClass(field: string): void {
    const $el = $(`#${field}`);
    if (!$el?.length) {
      return;
    }
    const $wrap = $el.parent().hasClass('bootstrap-select')
      ? $el.parent()
      : $el.closest('.bootstrap-select');
    const $btn = $wrap.find('> button.dropdown-toggle');
    const showError = this.isInvalid(field);
    $el.toggleClass('is-invalid perfil-field__control--invalid', showError);
    $btn.toggleClass('is-invalid perfil-field__control--invalid', showError);
    if ($wrap?.length) {
      $wrap.toggleClass(
        'is-invalid perfil-field__control--invalid ng-invalid',
        showError
      );
      if (!showError) {
        $wrap.removeClass('ng-invalid');
      }
    }
  }

  private refreshAllSelectpickers(): void {
    setTimeout(() => {
      if (typeof $('.selectpicker').selectpicker === 'function') {
        $('.selectpicker').selectpicker('refresh');
      }
    }, 50);
  }

  onDocCargarClick(
    doc: PerfilDocumentoDef,
    slotKeyForUpload?: string,
    inputEl?: HTMLInputElement
  ): void {
    this.openDocModal(doc, slotKeyForUpload, inputEl);
  }

  private setSelectpickerVal(field: string, value: unknown): void {
    if (value == null || value === '') {
      return;
    }
    const $el = $(`#${field}`);
    if ($el?.length && typeof $el.selectpicker === 'function') {
      this.initOrRefreshSelectpicker(field);
      $el.selectpicker('val', String(value));
    }
  }

  // --- PJ multi-paso ---

  private rebuildSteps(): void {
    if (!this.esJuridica) {
      return;
    }
    const next: SobreTiStep[] = [
      {
        id: 'datos-empresa',
        controls: [
          'genero',
          'minoria',
          'creditoFormal',
          'razonSocial',
          'actividadEconomica',
          'tieneCamaraComercio',
          'departamento',
          'ciudad',
          'barrio',
          'direccion',
          'tipoSociedad',
        ],
      },
    ];
    const fotoKeys = this.fotoDocs
      .filter((d) => d.obligatorio)
      .map((d) => d.valueKey);
    const docKeys = this.documentosSeccion
      .filter((d) => d.obligatorio)
      .map((d) => d.valueKey);
    next.push({
      id: 'fotos',
      controls: [...docKeys, ...fotoKeys],
    });
    next.push({
      id: 'info-adicional',
      controls: this.documentosInfoAdicional
        .filter((d) => d.obligatorio)
        .map((d) => d.valueKey),
    });
    const prevId = this.steps[this.currentStep]?.id;
    this.steps = next;
    if (this.pendingPaso != null) {
      this.applyPendingPaso();
      return;
    }
    const idx = prevId ? next.findIndex((s) => s.id === prevId) : 0;
    this.currentStep = idx >= 0 ? idx : 0;
  }

  private applyPendingPaso(): void {
    if (this.pendingPaso == null || !this.steps.length) {
      return;
    }
    const nextStep = Math.min(this.pendingPaso, this.steps.length - 1);
    this.pendingPaso = null;
    if (nextStep === this.currentStep) {
      if (this.steps[nextStep]?.id === 'datos-empresa') {
        this.scheduleRebindUbicacionSelectpickers();
      }
      return;
    }
    this.currentStep = nextStep;
    this.scrollFormToTop();
    if (this.currentStepId === 'datos-empresa') {
      this.scheduleRebindUbicacionSelectpickers();
    }
  }

  private isCurrentStepValid(): boolean {
    const step = this.steps[this.currentStep];
    if (!step) {
      return false;
    }
    if (step.id === 'info-adicional') {
      return this.areCheckedRedUrlsValid();
    }
    const keys = [...step.controls];
    if (step.id === 'datos-empresa' && this.muestraLocalidad) {
      keys.push('localidad');
    }
    if (step.id === 'datos-empresa' && this.muestraMinoriaDescripcion) {
      keys.push('minoriaDescripcion');
    }
    if (step.id === 'datos-empresa' && this.muestraFechaDesembolso) {
      keys.push('fechaDesembolso');
    }
    const baseOk = keys.every((key) => this.form.get(key)?.valid);
    if (!baseOk) {
      return false;
    }
    if (step.id === 'datos-empresa' && this.muestraFechaCamara) {
      const fechaCtrl = this.form.get('fechaRegistroCamara');
      if (!fechaCtrl?.value || fechaCtrl.invalid) {
        return false;
      }
    }
    return true;
  }

  private areCheckedRedUrlsValid(): boolean {
    return this.redesItems.every((red) => {
      if (!this.form.get(red.id)?.value) {
        return true;
      }
      return !!this.form.get(`${red.id}Url`)?.valid;
    });
  }

  private markCurrentStepTouched(): void {
    const step = this.steps[this.currentStep];
    if (!step) {
      return;
    }
    for (const key of step.controls) {
      this.form.get(key)?.markAsTouched();
    }
    if (step.id === 'datos-empresa') {
      if (this.muestraLocalidad) {
        this.form.get('localidad')?.markAsTouched();
      }
      if (this.muestraMinoriaDescripcion) {
        this.form.get('minoriaDescripcion')?.markAsTouched();
      }
      if (this.muestraFechaDesembolso) {
        this.form.get('fechaDesembolso')?.markAsTouched();
      }
      if (this.muestraFechaCamara) {
        this.form.get('fechaRegistroCamara')?.markAsTouched();
      }
    }
    if (step.id === 'info-adicional') {
      for (const red of this.redesItems) {
        if (this.form.get(red.id)?.value) {
          this.form.get(`${red.id}Url`)?.markAsTouched();
        }
      }
    }
  }

  private scrollFormToTop(): void {
    const el = this.sectionScroll?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  private syncMinoriaValidators(value: unknown): void {
    const ctrl = this.form.get('minoriaDescripcion');
    if (!ctrl) {
      return;
    }
    if (value === 'si') {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private syncFechaCamaraValidators(value: unknown): void {
    const ctrl = this.form.get('fechaRegistroCamara');
    if (!ctrl) {
      return;
    }
    if (value === 'si') {
      ctrl.setValidators([
        Validators.required,
        this.fechaRegistroAntiguedadValidator(),
      ]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private revalidateFechaCamaraAntiguedad(): void {
    const ctrl = this.form.get('fechaRegistroCamara');
    if (!ctrl || this.form.get('tieneCamaraComercio')?.value !== 'si') {
      return;
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private fechaRegistroAntiguedadValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (!raw) {
        return null;
      }
      const meses =
        this.segmentoService.paramsSnapshot.fechaRegistroMinimaMeses ?? 0;
      return isFechaRegistroCamaraInvalida(String(raw), meses)
        ? { fechaRegistroAntiguedad: true }
        : null;
    };
  }

  private syncLocalidadValidators(): void {
    const ctrl = this.form.get('localidad');
    if (!ctrl) {
      return;
    }
    if (this.muestraLocalidad) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  showFechaCamaraHint(event: Event): void {
    const btn = event.currentTarget as HTMLElement | null;
    if (!btn || !this.mensajeFechaRegistro) {
      return;
    }
    const rect = btn.getBoundingClientRect();
    this.fechaCamaraHintStyle = {
      top: `${Math.max(8, rect.top - 12)}px`,
      left: `${rect.left + rect.width / 2}px`,
    };
    this.fechaCamaraHintOpen = true;
  }

  hideFechaCamaraHint(): void {
    this.fechaCamaraHintOpen = false;
  }

  isRedChecked(redId: string): boolean {
    return !!this.form.get(redId)?.value;
  }

  onDepartamentoChange(event: Event): void {
    if (this.hydratingUbicacion) {
      return;
    }
    const value = (event.target as HTMLSelectElement)?.value;
    this.refreshSelectpicker('departamento');
    this.resetDependentSelect('ciudad');
    this.resetDependentSelect('localidad');
    this.ciudades = [];
    this.refreshSelectpicker('ciudad');
    this.refreshSelectpicker('localidad');
    if (value) {
      this.getCiudades(Number(value));
    }
  }

  onCiudadChange(): void {
    if (this.hydratingUbicacion) {
      return;
    }
    const ciudad = this.form.get('ciudad');
    ciudad?.updateValueAndValidity({ emitEvent: false });
    ciudad?.markAsTouched();
    this.refreshSelectpicker('ciudad');
    setTimeout(() => this.syncSelectpickerInvalidClass('ciudad'), 80);
    this.resetDependentSelect('localidad');
    this.syncLocalidadValidators();
    this.refreshSelectpicker('localidad');
  }

  /** Limpia hijo (ciudad/localidad) y lo deja required+rojo hasta que elijan. */
  private resetDependentSelect(field: 'ciudad' | 'localidad'): void {
    const ctrl = this.form.get(field);
    if (!ctrl) {
      return;
    }
    ctrl.setValue('');
    ctrl.markAsTouched();
    ctrl.markAsDirty();
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onLocalidadChange(): void {
    this.refreshSelectpicker('localidad');
  }

  onTipoSociedadChange(): void {
    this.refreshSelectpicker('tipoSociedad');
  }

  onActividadEconomicaChange(): void {
    this.refreshSelectpicker('actividadEconomica');
  }

  fotoSlotIndex(valueKey: string): number {
    return this.fotoDocs.findIndex((d) => d.valueKey === valueKey);
  }

  onFotoCargarClick(index: number): void {
    const doc = this.fotoDocs[index];
    if (!doc) {
      return;
    }
    this.openDocModal(doc, undefined, undefined, 'btn-perfil-foto');
  }

  triggerFotoUpload(index: number): void {
    const input = this.fotoInputs?.get(index)?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onFotoSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const doc = this.fotoDocs[index];
    if (!file || !doc) {
      return;
    }
    this.solicitudService
      .subirDocumento(file, doc, {
        esFoto: true,
        excludeNombreArchivo: this.fotos[index]?.name || undefined,
      })
      .subscribe((res) => {
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo subir el archivo'
          );
          return;
        }
        this.fotos[index] = slotStateFromUploadResult(file, res);
        const ok = isArchivoSlotValidoParaProgreso(this.fotos[index]);
        this.form
          .get(doc.valueKey)
          ?.setValue(ok ? this.fotos[index].name : '', { emitEvent: false });
        this.form.get(doc.valueKey)?.markAsTouched();
        this.form
          .get(doc.valueKey)
          ?.updateValueAndValidity({ emitEvent: false });
        if (res.estadoDocumentoOCR === 0) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error ||
              res.observacionDocumentoOCR ||
              this.copy.sobreEmpresa.validaciones.documentosInconsistentes
          );
        } else {
          void this.sweetAlert.successModal('Archivo subido');
        }
        if (this.docModalOpen) {
          this.closeDocModal();
        }
        this.syncProgress();
        this.solicitudService.persistirCamposProgreso().subscribe();
      });
  }

  removeFoto(index: number): void {
    if (!this.canRemoveFoto(index)) {
      return;
    }
    const doc = this.fotoDocs[index];
    const idDocumento = this.fotos[index]?.idDocumento;
    const clearLocal = (): void => {
      this.fotos[index] = emptyArchivoSlot();
      if (doc) {
        this.form.get(doc.valueKey)?.setValue('', { emitEvent: false });
        this.form.get(doc.valueKey)?.markAsTouched();
        this.form
          .get(doc.valueKey)
          ?.updateValueAndValidity({ emitEvent: false });
      }
      const input = this.fotoInputs?.get(index)?.nativeElement;
      if (input) {
        input.value = '';
      }
    };
    if (idDocumento) {
      this.solicitudService
        .eliminarDocumento(idDocumento, {
          esFoto: true,
          idTipoDocumento: doc?.idTipoDocumento ?? doc?.id,
          nombreArchivo: this.fotos[index]?.name || null,
        })
        .subscribe((res) => {
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo eliminar la foto'
            );
            return;
          }
          clearLocal();
          this.syncProgress();
          this.solicitudService.persistirCamposProgreso().subscribe();
        });
      return;
    }
    clearLocal();
    this.syncProgress();
    this.solicitudService.persistirCamposProgreso().subscribe();
  }

  hasFoto(index: number): boolean {
    return isArchivoSlotValidoParaProgreso(this.fotos[index]);
  }

  hasFotoLoaded(index: number): boolean {
    return !!this.fotos[index]?.name;
  }

  isFotoRechazada(index: number): boolean {
    return isArchivoSlotRechazado(this.fotos[index]);
  }

  isFotoAprobadaUi(index: number): boolean {
    return shouldShowArchivoSlotAprobadoUi(
      this.fotos[index],
      this.solicitudService.idEstadoSolicitud
    );
  }

  canRemoveFoto(index: number): boolean {
    return canRemoveArchivoSlot(
      this.fotos[index],
      this.solicitudService.idEstadoSolicitud
    );
  }

  fotoStatusLabel(index: number): string {
    if (this.isFotoRechazada(index)) {
      return this.copy.sobreNegocio.fotosArchivoRechazado;
    }
    if (this.isFotoAprobadaUi(index)) {
      return this.copy.sobreNegocio.fotosArchivoAprobado;
    }
    return this.copy.sobreNegocio.fotosArchivoSubido;
  }

  fotoObservacion(index: number): string {
    return String(this.fotos[index]?.observacionDocumentoOCR || '').trim();
  }

  private buildRedesPayload(): Array<{
    url: string;
    idCatalogoRedSocial: number | null;
  }> {
    const redesSocialesDetalle: Array<{
      url: string;
      idCatalogoRedSocial: number | null;
    }> = [];
    for (const red of this.redesItems) {
      if (!this.form.get(red.id)?.value) {
        continue;
      }
      const url = String(this.form.get(`${red.id}Url`)?.value ?? '').trim();
      if (!url) {
        continue;
      }
      redesSocialesDetalle.push({
        url: normalizeRedSocialUrl(url),
        idCatalogoRedSocial:
          red.idCatalogo != null ? Number(red.idCatalogo) : null,
      });
    }
    return redesSocialesDetalle;
  }

  private guardarDatosEmpresaYAvanzar(): void {
    this.saving = true;
    this.solicitudService
      .guardarBorrador(this.buildPatchJuridica())
      .subscribe((res) => {
        this.saving = false;
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo guardar la información'
          );
          return;
        }
        this.markSectionBaseline();
        const fotosIdx = this.steps.findIndex((s) => s.id === 'fotos');
        this.currentStep = fotosIdx >= 0 ? fotosIdx : Math.min(1, this.steps.length - 1);
        this.scrollFormToTop();
      });
  }

  private guardarRedesYContinuar(): void {
    this.saving = true;
    this.solicitudService
      .guardarBorrador({ redesSocialesDetalle: this.buildRedesPayload() })
      .subscribe((res) => {
        this.saving = false;
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo guardar la información'
          );
          return;
        }
        this.markSectionBaseline();
        this.goToNextSection();
      });
  }

  private maybeOpenIntroModal(): void {
    if (!this.showIntroOnEnter) {
      return;
    }
    if (!this.seccionAcceso.shouldShowIntro('sobre-ti')) {
      consumeShowIntroFlag(readPerfilNavState(this.router));
      return;
    }
    this.seccionAcceso.markIntroShown('sobre-ti');
    this.introModalOpen = true;
    consumeShowIntroFlag(readPerfilNavState(this.router));
  }

  private cargarCatalogosJuridica(): void {
    this.parametros.getGeneros().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.generos = list.map(
        (g: { idGenero: number | string; nombre: string }) => ({
          id: g.idGenero,
          nombre: g.nombre,
        })
      );
      setTimeout(() => {
        this.refreshSelectpicker('genero');
        this.reapplySelectFromForm('genero');
      }, 200);
    });
    this.parametros.getTipoSociedad().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.tiposSociedad = list.map(
        (t: { idTipoSociedad: number; nombre?: string; nombreTipoSociedad?: string }) => ({
          id: t.idTipoSociedad,
          nombre: t.nombre || t.nombreTipoSociedad || String(t.idTipoSociedad),
        })
      );
      setTimeout(() => {
        this.refreshSelectpicker('tipoSociedad');
        this.reapplySelectFromForm('tipoSociedad');
      }, 200);
    });
    this.parametros.getCiius().subscribe((data: any) => {
      const list = Array.isArray(data) ? data : data?.data ?? [];
      this.ciius = list.map(
        (c: { codigoCiiu: string; nombreCiiu: string }) => ({
          codigoCiiu: String(c.codigoCiiu),
          nombreCiiu: c.nombreCiiu,
        })
      );
      setTimeout(() => {
        this.refreshSelectpicker('actividadEconomica');
        this.reapplySelectFromForm('actividadEconomica');
      }, 200);
    });
    this.parametros.getDepartamentos('169').subscribe({
      next: (data: any) => {
        this.departamentos = (data?.data ?? []).map(
          (d: { idDepartamento: number; nombreDepartamento: string }) => ({
            id: d.idDepartamento,
            nombre: d.nombreDepartamento,
          })
        );
        this.departamentosCargados = true;
        this.tryHydrateUbicacion();
      },
      error: () => {
        this.departamentosCargados = true;
        this.tryHydrateUbicacion();
      },
    });
  }

  /** Tras cargar opciones del catálogo, reaplicar valor del form al selectpicker. */
  private reapplySelectFromForm(field: string): void {
    const raw = this.form.get(field)?.value;
    if (raw == null || raw === '') {
      return;
    }
    this.setSelectpickerVal(field, String(raw));
  }

  private getCiudades(idDepartamento: number, onDone?: () => void): void {
    this.parametros.getCiudades(String(idDepartamento)).subscribe({
      next: (data: any) => {
        this.ciudades = (data?.data ?? []).map(
          (c: { idCiudad: number; nombreCiudad: string }) => ({
            id: c.idCiudad,
            nombre: c.nombreCiudad,
          })
        );
        setTimeout(() => {
          onDone?.();
          this.refreshSelectpicker('ciudad');
        }, 200);
      },
      error: () => onDone?.(),
    });
  }

  private tryHydrateUbicacion(): void {
    if (this.ubicacionHydrateDone) {
      return;
    }
    if (!this.departamentosCargados) {
      return;
    }
    const emp = this.pendingEmpUbicacion;
    if (emp === undefined) {
      return;
    }
    this.ubicacionHydrateDone = true;
    if (!emp?.idDepartamento) {
      this.hydratingUbicacion = false;
      return;
    }
    const idDepto = String(emp.idDepartamento);
    const idCiudad = emp.idCiudad != null ? String(emp.idCiudad) : '';
    this.hydratingUbicacion = true;
    this.form.get('departamento')?.setValue(idDepto, { emitEvent: false });
    this.getCiudades(Number(idDepto), () => {
      if (idCiudad) {
        this.form.get('ciudad')?.setValue(idCiudad, { emitEvent: false });
        this.syncLocalidadValidators();
        if (Number(idCiudad) === 11001 && emp.idLocalidad != null) {
          this.form
            .get('localidad')
            ?.setValue(String(emp.idLocalidad), { emitEvent: false });
        }
      }
      setTimeout(() => {
        this.setSelectpickerVal('departamento', idDepto);
        if (idCiudad) {
          this.setSelectpickerVal('ciudad', idCiudad);
        }
        if (Number(idCiudad) === 11001 && emp.idLocalidad != null) {
          this.setSelectpickerVal('localidad', String(emp.idLocalidad));
        }
        this.hydratingUbicacion = false;
        this.refreshAllSelectpickers();
        this.scheduleRebindUbicacionSelectpickers();
        this.scheduleBaselineAfterUiSettle();
      }, 100);
    });
  }

  private ubicacionHydrateDone = false;

  private scheduleRebindUbicacionSelectpickers(): void {
    setTimeout(() => this.rebindDatosEmpresaSelectpickersFromForm(), 0);
    setTimeout(() => this.rebindDatosEmpresaSelectpickersFromForm(), 120);
  }

  private rebindDatosEmpresaSelectpickersFromForm(): void {
    if (!this.esJuridica || this.currentStepId !== 'datos-empresa') {
      return;
    }
    for (const field of [
      'genero',
      'departamento',
      'ciudad',
      'localidad',
      'actividadEconomica',
      'tipoSociedad',
    ]) {
      this.reapplySelectFromForm(field);
    }
  }

  private initOrRefreshSelectpicker(field: string): void {
    const $el = $(`#${field}`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    const wrapped = $el.parent().hasClass('bootstrap-select');
    if (wrapped) {
      $el.selectpicker('refresh');
    } else {
      $el.selectpicker();
    }
  }

  private cargarRedesSociales(): void {
    this.redesSub = this.parametros
      .getRedesSocialesPerfil()
      .pipe(catchError(() => of([])))
      .subscribe((rows) => {
        const list = this.extraerListaRedes(rows);
        this.applyRedes(this.mapRedesFromApi(list));
        this.applyRedesFromBackend();
        this.syncAllRedUrlValidators();
        this.syncProgress();
        this.markSectionBaseline();
        this.scheduleBaselineAfterUiSettle();
      });
  }

  private extraerListaRedes(res: unknown): CatalogoRedSocialApi[] {
    if (Array.isArray(res)) {
      return res as CatalogoRedSocialApi[];
    }
    if (
      res &&
      typeof res === 'object' &&
      Array.isArray((res as { data?: unknown }).data)
    ) {
      return (res as { data: CatalogoRedSocialApi[] }).data;
    }
    return [];
  }

  private mapRedesFromApi(rows: CatalogoRedSocialApi[]): RedSocialItem[] {
    return [...rows]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((row, index) => {
        const id =
          row.codigo ||
          (row.idCatalogoRedSocial != null
            ? String(row.idCatalogoRedSocial)
            : `red_${index}`);
        return {
          id,
          label: row.nombre || id,
          idCatalogo: row.idCatalogoRedSocial,
          esOtro: !!row.esOtro,
        };
      });
  }

  private applyRedes(list: RedSocialItem[]): void {
    for (const red of this.redesItems) {
      if (this.form.contains(red.id)) {
        this.form.removeControl(red.id);
      }
      if (this.form.contains(`${red.id}Url`)) {
        this.form.removeControl(`${red.id}Url`);
      }
    }
    this.redesItems = list;
    for (const red of this.redesItems) {
      this.form.addControl(red.id, this.fb.control(false));
      this.form.addControl(`${red.id}Url`, this.fb.control(''));
      this.loadSub.add(
        this.form
          .get(red.id)!
          .valueChanges.subscribe((checked) =>
            this.syncRedUrlValidators(red.id, !!checked)
          )
      );
    }
  }

  private syncAllRedUrlValidators(): void {
    for (const red of this.redesItems) {
      this.syncRedUrlValidators(red.id, !!this.form.get(red.id)?.value);
    }
  }

  private syncRedUrlValidators(redId: string, checked: boolean): void {
    const urlCtrl = this.form.get(`${redId}Url`);
    if (!urlCtrl) {
      return;
    }
    if (checked) {
      urlCtrl.setValidators([Validators.required]);
    } else {
      urlCtrl.clearValidators();
      urlCtrl.setValue('');
      urlCtrl.markAsUntouched();
    }
    urlCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private applyRedesFromBackend(): void {
    const fromCarga = Array.isArray(this.solicitudService.redesDetalleSnapshot)
      ? this.solicitudService.redesDetalleSnapshot
      : [];
    if (!fromCarga.length || !this.redesItems.length) {
      return;
    }
    for (const red of this.redesItems) {
      const match = findRedDetalleForCatalogo(
        fromCarga,
        red.idCatalogo ?? null,
        red.id
      );
      if (!match) {
        this.form.get(red.id)?.setValue(false, { emitEvent: false });
        this.form.get(`${red.id}Url`)?.setValue('', { emitEvent: false });
        continue;
      }
      this.form.get(red.id)?.setValue(true, { emitEvent: false });
      this.form.get(`${red.id}Url`)?.setValue(match.url, { emitEvent: false });
    }
    this.syncAllRedUrlValidators();
  }
}
